import IconOther from '@assets/images/common/icon/channel/chat-menu-emoji.svg';
import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  ChatReaction,
  chatReactionApiClient,
} from '@src/api/shared/community/chatReactionApiClient';
import { DEFAULT_REACTIONS } from '@src/constants/defaultReactions';
import { useSharedAuth } from '@src/context/auth/useSharedAuth';
import { useReactionContext } from '@src/context/ReactionContext';
import { useCanOpenThread } from '@src/features/shared/community/components/chat/hooks/useCanOpenThread';
import { useReactionLoginAlertTooltip } from '@src/hooks/useReactionLoginAlertTooltip';
import { isTenantMember } from '@src/utils/checkTenantMemberPermission';
import { Emoji, EmojiClickData } from 'emoji-picker-react';
import { useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ChatMenuPermission } from './ChatMessage';
import EmojiBottomMenuSp from './EmojiBottomMenuSp';
import SpBottomMenuLayout from './SpBottomMenuLayout';

const ChatActionMenuSp = ({
  handleCloseClick,
  handleChatLinkCopyClick,
  handlePinClick,
  handleChatEditClick,
  handleChatDeleteClick,
  handleChatReportClick,
  handleOpenThread,
  isPinned,
  chatId,
  chatMenuPermission,
  isLoggedIn = true,
  currentReactions,
}: {
  handleCloseClick: () => void;
  handleChatLinkCopyClick: () => void;
  handlePinClick: () => void;
  handleChatEditClick: () => void;
  handleChatDeleteClick: () => void;
  handleChatReportClick: () => void;
  handleOpenThread?: () => void;
  isPinned: boolean;
  chatId: number;
  chatMenuPermission?: ChatMenuPermission;
  isLoggedIn?: boolean;
  currentReactions?: ChatReaction[];
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { code } = useParams<{ code: string }>();
  const { showTooltipIfNotLoggedIn } = useReactionLoginAlertTooltip();
  const { profile } = useSharedAuth();
  const isUserTenantMember = isTenantMember(profile);
  const { reactions, updateReactionUsers, updateReactionsOptimistically } = useReactionContext();
  const isTenantAdminUser = profile?.isTenant && profile?.tenantProfile?.user?.isAdmin;
  const canOpenThread = useCanOpenThread();

  const handleEmojiSelect = useCallback(
    async (emojiData: EmojiClickData | string) => {
      if (showTooltipIfNotLoggedIn()) {
        return;
      }

      if (!code || !chatId) {
        return;
      }

      const emoji = (typeof emojiData === 'string' ? emojiData : emojiData.unified).toLowerCase();

      setShowEmojiPicker(false);
      setIsExpanded(false);
      handleCloseClick();

      // 楽観更新: 即座にリアクションを更新
      const reactionsToUse = reactions[chatId] || currentReactions;
      updateReactionsOptimistically(chatId, emoji, reactionsToUse);

      const res = await chatReactionApiClient.post(code, chatId, { emoji });
      handleApiResponse(res, (data) => {
        if (data) {
          // ユーザー情報のみを更新
          updateReactionUsers(chatId, emoji, data.users);
        }
      });
    },
    [
      chatId,
      code,
      currentReactions,
      handleCloseClick,
      reactions,
      showTooltipIfNotLoggedIn,
      updateReactionUsers,
      updateReactionsOptimistically,
    ],
  );

  const handleEmojiPickerClick = () => {
    if (showTooltipIfNotLoggedIn()) {
      return;
    }

    setShowEmojiPicker((prev) => !prev);
  };

  if (showEmojiPicker) {
    return (
      <EmojiBottomMenuSp
        handleCloseClick={handleCloseClick}
        handleEmojiClick={handleEmojiSelect}
      />
    );
  }

  return (
    <SpBottomMenuLayout
      handleCloseClick={handleCloseClick}
      isSwipeAfter={isExpanded}
      onSwipeDown={() => {
        if (isExpanded) {
          setIsExpanded(false);
        } else {
          handleCloseClick();
        }
      }}
    >
      <div ref={menuRef}>
        <div className='px-[25px] pb-8 pt-[34px]'>
          {isLoggedIn && !isUserTenantMember && (
            <ul className='relative mx-auto mb-[34px] grid max-w-[340px] grid-cols-5 place-items-center gap-[10px]'>
              {DEFAULT_REACTIONS.map((emoji, index) => (
                <li
                  key={index}
                  className='aspect-square size-full rounded-full bg-[#F9F9F9]'
                >
                  <button
                    className='grid size-full place-items-center text-[28px]'
                    onClick={() => handleEmojiSelect(emoji)}
                  >
                    <Emoji
                      unified={emoji.toLowerCase()}
                      size={28}
                    />
                  </button>
                </li>
              ))}

              <li className='relative aspect-square size-full rounded-full bg-[#F9F9F9]'>
                <button
                  className='grid size-full place-items-center'
                  onClick={handleEmojiPickerClick}
                >
                  <img
                    width={32}
                    height={32}
                    src={IconOther}
                    alt=''
                    className='size-8 object-contain'
                  />
                </button>
              </li>
            </ul>
          )}
          <ul className='mx-auto max-w-[340px]'>
            {handleOpenThread && canOpenThread && (
              <li className='h-[50px]'>
                <button
                  className='size-full text-left'
                  onClick={() => {
                    handleOpenThread();
                    handleCloseClick();
                  }}
                >
                  スレッドに返信する
                </button>
              </li>
            )}
            {isTenantAdminUser && (
              <li className='h-[50px]'>
                <button
                  className='size-full text-left'
                  onClick={() => {
                    handlePinClick();
                    handleCloseClick();
                  }}
                >
                  {isPinned ? 'チャンネルトップから固定解除する' : 'チャンネルトップへ固定する'}
                </button>
              </li>
            )}
            {chatMenuPermission?.canEdit && (
              <li className='h-[50px]'>
                <button
                  className='size-full text-left'
                  onClick={() => {
                    handleChatEditClick();
                    handleCloseClick();
                  }}
                >
                  メッセージを編集
                </button>
              </li>
            )}
            <li className='h-[50px]'>
              <button
                className='size-full text-left'
                onClick={() => {
                  handleChatLinkCopyClick();
                  handleCloseClick();
                }}
              >
                リンクをコピー
              </button>
            </li>
            {chatMenuPermission?.canDelete && (
              <li className='h-[50px]'>
                <button
                  className='size-full text-left'
                  onClick={() => {
                    handleChatDeleteClick();
                    handleCloseClick();
                  }}
                >
                  メッセージを削除
                </button>
              </li>
            )}
            {chatMenuPermission?.canReport && (
              <li className='h-[50px]'>
                <button
                  className='size-full text-left'
                  onClick={() => {
                    handleChatReportClick();
                    handleCloseClick();
                  }}
                >
                  報告する
                </button>
              </li>
            )}
          </ul>
        </div>
      </div>
    </SpBottomMenuLayout>
  );
};

export default ChatActionMenuSp;
